import { useEffect, useState } from 'react';
import {
  CalendarDaysIcon,
  NoSymbolIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { PlanningTask, PlanningWorkItemEvidenceFormPayload } from '../../services/strategicPlanningApi';
import {
  formatPlanningTaskPriority,
  formatPlanningTaskStatusLabel,
  formatPlanningFulfillmentResult,
  canEditPlanningTaskOwner,
  PLANNING_FULFILLMENT_OPTIONS,
} from '../../types/PlanningTaskStatus';
import { formatDateBr, formatDateTimeBr } from '../../utils/formatDateBr';
import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { PlanningTaskEvidenceSection } from './PlanningTaskEvidenceSection';
import { TaskAreaPicker } from './TaskAreaPicker';
import { EditIconButton } from '../ui/EditIconButton';
import { IconActionButton } from '../ui/IconActionButton';
import { RequiredMark } from '../ui/RequiredMark';
import { PlanningCancellationReasonField } from './PlanningCancellationReasonField';

type PlanningTaskDetailPanelProps = {
  task: PlanningTask | null;
  canWrite: boolean;
  currentUserId?: string;
  linkLabel?: string;
  onClose: () => void;
  onStartExecution: (taskId: string, executorUserId: string) => Promise<void>;
  onFinishExecution: (taskId: string, fulfillmentResult: string) => Promise<void>;
  onCancel: (taskId: string, cancellationReason: string) => Promise<void>;
  onAddEvidence: (taskId: string, payload: PlanningWorkItemEvidenceFormPayload) => Promise<void>;
  onUpdateEvidence: (
    taskId: string,
    evidenceId: string,
    payload: PlanningWorkItemEvidenceFormPayload,
  ) => Promise<void>;
  onDeleteEvidence: (taskId: string, evidenceId: string) => Promise<void>;
  onAssign: (taskId: string, ownerUserId: string) => Promise<void>;
  onAreasChange: (taskId: string, areas: string[]) => Promise<void>;
  onDuplicate: (taskId: string) => Promise<void>;
  onEdit: (task: PlanningTask) => void;
  onExtendDeadline?: (task: PlanningTask) => void;
  isExecutive?: boolean;
};

export function PlanningTaskDetailPanel({
  task,
  canWrite,
  currentUserId,
  linkLabel,
  onClose,
  onStartExecution,
  onFinishExecution,
  onCancel,
  onAddEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onAssign,
  onAreasChange,
  onDuplicate,
  onEdit,
  onExtendDeadline,
  isExecutive = false,
}: PlanningTaskDetailPanelProps) {
  const [fulfillmentResult, setFulfillmentResult] = useState('NotFulfilled');
  const [busy, setBusy] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    setFulfillmentResult('NotFulfilled');
    setShowCancelForm(false);
    setCancellationReason('');
  }, [task?.id]);

  if (!task) return null;

  const taskId = task.id;
  const isClosed =
    task.status === 'Completed'
    || task.status === 'Cancelled'
    || task.status === 'NotStarted'
    || task.status === 'Abandoned'
    || Boolean(task.executionFinishedAt);
  const canStart =
    canWrite
    && !task.executionFinishedAt
    && Boolean(task.ownerUserId)
    && (task.status === 'Planned' || task.status === 'Overdue');
  const canFinish =
    canWrite
    && !task.executionFinishedAt
    && (task.status === 'InProgress' || task.status === 'Overdue')
    && (task.kind !== 'Meta' || isExecutive);
  const canCancel = canWrite && !isClosed;
  const canExtend =
    canWrite
    && !isClosed
    && Boolean(task.plannedEndDate)
    && Boolean(onExtendDeadline)
    && (Boolean(task.executionStartedAt) || task.status === 'Overdue');

  async function runAction(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  return (
    <DraggableModalPanel
      open
      title={task.title}
      subtitle={formatPlanningTaskStatusLabel(task.status)}
      onClose={onClose}
      className="w-full max-w-lg"
    >
      <div className="space-y-4">
        {task.description ? <p className="text-sm text-secondary-700">{task.description}</p> : null}

        {canWrite ? (
          <button
            type="button"
            className="erp-btn-secondary w-full"
            disabled={busy}
            onClick={() => void runAction(() => onDuplicate(taskId))}
          >
            Duplicar tarefa
          </button>
        ) : null}

        {canWrite && !isClosed ? (
          <div
            className={`space-y-2 rounded-xl border p-3 ${
              task.ownerUserId
                ? 'border-secondary-200'
                : 'border-amber-300 bg-amber-50/70'
            }`}
          >
            <p className="text-sm font-semibold text-secondary-800">
              Responsável <RequiredMark />
            </p>
            <p className="text-xs text-secondary-600">
              Clique no campo, busque pelo nome e clique fora para salvar.
            </p>
            <CollaboratorAutocomplete
              valueUserId={task.ownerUserId}
              valueUserName={task.ownerUserName}
              disabled={busy}
              saving={busy}
              onCommit={(user) => {
                if (!user) return;
                void runAction(() => onAssign(task.id, user.userId));
              }}
            />
            {!task.ownerUserId
              && (task.status === 'Planned' || task.status === 'Overdue')
              && !task.executionFinishedAt ? (
              <p className="text-xs text-amber-800">
                Atribua um responsável para poder iniciar a execução.
              </p>
            ) : null}
          </div>
        ) : null}

        <TaskAreaPicker
          selectedIds={task.areas ?? []}
          disabled={!canWrite || busy}
          onChange={(areas) => {
            void runAction(() => onAreasChange(task.id, areas));
          }}
        />

        {canStart && currentUserId ? (
          <button
            type="button"
            className="erp-btn-primary w-full gap-2"
            disabled={busy}
            onClick={() => void runAction(() => onStartExecution(task.id, currentUserId))}
          >
            <PlayIcon className="h-5 w-5" />
            Iniciar
          </button>
        ) : null}

        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2">
            <dt className="text-secondary-500">Vínculo</dt>
            <dd className="font-medium">
              {linkLabel
                ?? (task.parentWorkItemId
                  ? 'Vinculada a meta ou marco'
                  : task.objectiveId
                    ? 'Somente ao objetivo — edite para vincular meta ou marco'
                    : 'Sem vínculo')}
            </dd>
          </div>
          <div>
            <dt className="text-secondary-500">Prioridade</dt>
            <dd className="font-medium">{formatPlanningTaskPriority(task.priority)}</dd>
          </div>
          {!canWrite || isClosed ? (
            <div>
              <dt className="text-secondary-500">Responsável</dt>
              <dd className="font-medium">{task.ownerUserName?.trim() || 'Não atribuído'}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-secondary-500">Prazo</dt>
            <dd className="font-medium">{formatDateBr(task.plannedEndDate)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Início planejado</dt>
            <dd className="font-medium">{formatDateBr(task.plannedStartDate)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Início da execução</dt>
            <dd className="font-medium">{formatDateTimeBr(task.executionStartedAt)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Término efetivo</dt>
            <dd className="font-medium">{formatDateTimeBr(task.executionFinishedAt)}</dd>
          </div>
        </dl>

        {task.fulfillmentResult ? (
          <p className="rounded-lg bg-secondary-50 px-3 py-2 text-sm">
            Execução: <strong>{formatPlanningFulfillmentResult(task.fulfillmentResult)}</strong>
          </p>
        ) : null}

        {canWrite && canEditPlanningTaskOwner(task.status, task.executionFinishedAt) ? (
          <div className="flex flex-wrap items-center justify-end gap-1">
            <EditIconButton
              label="Editar"
              disabled={busy}
              onClick={() => onEdit(task)}
            />
            {canExtend ? (
              <IconActionButton
                label="Estender prazo"
                icon={CalendarDaysIcon}
                disabled={busy}
                onClick={() => onExtendDeadline?.(task)}
              />
            ) : null}
            {canCancel ? (
              <IconActionButton
                label="Cancelar"
                icon={NoSymbolIcon}
                tone="danger"
                disabled={busy}
                onClick={() => setShowCancelForm((current) => !current)}
              />
            ) : null}
          </div>
        ) : null}

        {showCancelForm && canCancel ? (
          <div className="space-y-2 rounded-xl border border-rose-200 bg-rose-50/50 p-3">
            <PlanningCancellationReasonField
              value={cancellationReason}
              onChange={setCancellationReason}
            />
            <button
              type="button"
              className="erp-btn-danger w-full"
              disabled={busy || !cancellationReason.trim()}
              onClick={() =>
                void runAction(async () => {
                  await onCancel(task.id, cancellationReason.trim());
                  setShowCancelForm(false);
                  setCancellationReason('');
                })
              }
            >
              Confirmar cancelamento
            </button>
          </div>
        ) : null}

        {canFinish ? (
          <div className="space-y-2 rounded-xl border border-secondary-200 p-3">
            <p className="text-sm font-semibold text-secondary-800">Resultado da execução</p>
            <label className="block">
              <select
                className="erp-input"
                value={fulfillmentResult}
                onChange={(event) => setFulfillmentResult(event.target.value)}
              >
                {PLANNING_FULFILLMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="erp-btn-primary w-full"
              disabled={busy}
              onClick={() =>
                void runAction(() => onFinishExecution(task.id, fulfillmentResult))
              }
            >
              Finalizar
            </button>
          </div>
        ) : null}

        <PlanningTaskEvidenceSection
          taskId={task.id}
          evidences={task.evidences}
          canWrite={canWrite}
          busy={busy}
          onAdd={(payload) => runAction(() => onAddEvidence(task.id, payload))}
          onUpdate={(evidenceId, payload) => runAction(() => onUpdateEvidence(task.id, evidenceId, payload))}
          onDelete={(evidenceId) => runAction(() => onDeleteEvidence(task.id, evidenceId))}
        />
      </div>
    </DraggableModalPanel>
  );
}
